import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { CalendarIcon, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Reveal, Eyebrow } from "@/lib/motion";
import { SERVICE_OPTIONS } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { name: "", email: "", company: "", service_interest: "", message: "" };

export default function BookCall() {
  const [form, setForm] = useState(empty);
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        preferred_date: date ? format(date, "yyyy-MM-dd") : null,
      });
      setDone(true);
      toast.success("Thanks! We'll be in touch within one business day.");
      setForm(empty);
      setDate(null);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" data-testid="book-section" className="max-w-7xl mx-auto px-6 md:px-12 py-8 md:py-12">
      <div className="rounded-3xl bg-brand-ink text-brand-cream overflow-hidden grid lg:grid-cols-12">
        <div className="lg:col-span-5 p-8 md:p-14 flex flex-col justify-between relative">
          <div className="absolute -bottom-16 -left-10 w-64 h-64 rounded-full bg-brand-sage/30 blur-3xl" aria-hidden="true" />
          <div className="relative">
            <Eyebrow className="!text-brand-sage">Book a call</Eyebrow>
            <h2 className="mt-5 font-display font-normal text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              Let’s build your first agent.
            </h2>
            <p className="mt-6 text-brand-cream/70 text-lg leading-relaxed max-w-md">
              Tell us a little about your business. We’ll reply within one business day with a plan and a no-pressure demo.
            </p>
          </div>
          <div className="relative mt-10 font-mono text-xs uppercase tracking-[0.15em] text-brand-cream/60">
            hello@peachflowai.com
          </div>
        </div>

        <div className="lg:col-span-7 bg-brand-cream text-brand-ink p-8 md:p-14">
          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="h-full flex flex-col items-center justify-center text-center py-16"
              data-testid="book-success"
            >
              <div className="w-16 h-16 rounded-full bg-brand-sage grid place-items-center mb-6">
                <Check size={28} className="text-brand-ink" />
              </div>
              <h3 className="font-display text-3xl mb-3">Message received</h3>
              <p className="text-brand-ink/70 max-w-sm">We’ve got your details and will be in touch within one business day.</p>
              <button
                onClick={() => setDone(false)}
                data-testid="book-another"
                className="mt-8 font-mono text-xs uppercase tracking-[0.15em] text-brand-teal border-b border-brand-teal pb-1"
              >
                Send another
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="book-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="font-mono text-xs uppercase tracking-[0.15em] text-brand-teal">Name *</Label>
                  <Input id="name" data-testid="book-name" value={form.name} onChange={set("name")} placeholder="Jane Baker"
                    className="bg-transparent border-0 border-b border-brand-teal/40 rounded-none px-0 focus-visible:ring-0 focus-visible:border-brand-teal" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-mono text-xs uppercase tracking-[0.15em] text-brand-teal">Email *</Label>
                  <Input id="email" type="email" data-testid="book-email" value={form.email} onChange={set("email")} placeholder="jane@bakery.com"
                    className="bg-transparent border-0 border-b border-brand-teal/40 rounded-none px-0 focus-visible:ring-0 focus-visible:border-brand-teal" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="company" className="font-mono text-xs uppercase tracking-[0.15em] text-brand-teal">Company</Label>
                  <Input id="company" data-testid="book-company" value={form.company} onChange={set("company")} placeholder="Maple & Co."
                    className="bg-transparent border-0 border-b border-brand-teal/40 rounded-none px-0 focus-visible:ring-0 focus-visible:border-brand-teal" />
                </div>
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase tracking-[0.15em] text-brand-teal">Interested in</Label>
                  <Select value={form.service_interest} onValueChange={(v) => setForm((f) => ({ ...f, service_interest: v }))}>
                    <SelectTrigger data-testid="book-service" className="bg-transparent border-0 border-b border-brand-teal/40 rounded-none px-0 focus:ring-0">
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-[0.15em] text-brand-teal">Preferred date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="book-date-trigger"
                      className="w-full flex items-center gap-3 border-b border-brand-teal/40 py-2 text-left text-brand-ink/80"
                    >
                      <CalendarIcon size={16} className="text-brand-teal" />
                      {date ? format(date, "PPP") : <span className="text-brand-ink/50">Pick a date for our call</span>}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="font-mono text-xs uppercase tracking-[0.15em] text-brand-teal">What would you like to automate?</Label>
                <Textarea id="message" data-testid="book-message" value={form.message} onChange={set("message")} rows={3} placeholder="A bit about your business and where you're losing time..."
                  className="bg-transparent border border-brand-teal/40 rounded-2xl focus-visible:ring-0 focus-visible:border-brand-teal resize-none" />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="book-submit"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-brand-teal text-brand-cream px-8 py-4 font-mono text-xs uppercase tracking-[0.15em] hover:bg-brand-ink transition-colors duration-300 disabled:opacity-60"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                {loading ? "Sending" : "Book my call"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
